import { el, mount, unmount, setChildren } from 'redom';

import application from 'application';

import serverInterface from 'server-interface';

import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

/**
 * Every render method shall have one unmount method and update method
 * 
 * 
 * 
 * */

import * as meetingStatuses from 'constants/meeting-statuses';

import ProducerMode from '../live-view/producer-mode';
import ProducerModeEndedView from './ended-view';
import DevelopmentConsole from '../development-console';

import styleAsString from '../styles/index.scss?inline';

class SingleProducerMeetingStreamer extends HTMLElement {

  constructor() {

    super();

    this.attachShadow({ mode: 'open' });

    this.currentUserId = null;
    this.postId = null;
    this.post = null;
    this.meeting = null;
    this.loaded = false;
    this.inEditor = false;
    this.liveView = null;

    // elements
    this.spinner = null;
    this.producerMode = null;

    if (application.isAuthenticated() === true) {
      this.currentUserId = application.getUser()._id;
    }

    this.developmentConsole = window.developmentConsole = new DevelopmentConsole();
    this.mainEl = el('.single-producer-meeting-streamer-main', [
      el('ht-spinner', { size: 'small' })
    ]);

    mount(this.shadowRoot, this.getStyleEl());
    mount(this.shadowRoot, this.mainEl);
    mount(this.shadowRoot, this.developmentConsole);

  }

  async connectedCallback() {

    this.postId = this.getAttribute('postid');

    await this.initialize();
    this.registerEventListeners(this.postId);

  }

  disconnectedCallback() {

    this.unregisterEventListeners();
    this.releaseResources();

  }

  registerEventListeners(postId) {

    function handleMeetingEndedEvent(data) {

      this.meeting = data.meeting;

      this.mountRoot();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_ENDED}:${postId}`, handleMeetingEndedEvent.bind(this));

    function handleMeetingLiveStatusChangedEvent(data) {

      this.meeting = data.meeting;

      if (this.meeting.browserWindowId === application.getInstanceId()) {
        return;
      }

      application.eventSystem.internal.emit(internalEventCodes.HIDE_MEETING_ATTENDEES_LIST, {});

      application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
        to: `/community/${this.post.humanFriendlyId}`
      });

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.MEETING_LIVE_STATUS_CHANGED}:${postId}`, handleMeetingLiveStatusChangedEvent.bind(this));

  }

  unregisterEventListeners() {

    application.eventSystem.internal.offAllOn(this);

  }

  releaseResources() {

    if (this.liveView !== null) {
      this.liveView.releaseResources();
    }

  }

  getStyleEl() {

    const styleCode = `

      ${styleAsString}

    `;

    const style = el('style', styleCode);

    return style;

  }

  async loadMeeting() {

    const skipCache = true;

    this.meeting = await application.stores.singleProducerMeetings.getSingleProducerMeetingByPostId(this.postId, skipCache);

    this.loaded = true;

  }

  async loadPost() {

    this.post = await application.stores.posts.getPostBasicDetailsById(this.postId);
    this.post.owner = await application.usersStore.getUserById(this.post.ownerId);

  }

  async initialize() {

    try {

      await Promise.all([this.loadMeeting(), this.loadPost()]);

      if (this.meeting.status === meetingStatuses.CREATED) {
        this.meeting = await serverInterface.startup.community.singleProducerMeeting.startSingleProducerMeetingByPostId(this.postId);
      }
      await serverInterface.startup.community.singleProducerMeeting.trySetMeetingLiveStatusToConnected(this.postId, application.getInstanceId());

      if (this.meeting.deleted === true) {
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Meeting is deleted',
          seconds: 5,
          type: 'error'
        });
        application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
          to: '/community'
        });
        return;
      }

      this.mountRoot();

    } catch (exception) {

      let message = 'Unknown error! If repeated, kindly contact administration';
      let navigateTo = `/community/${this.post.humanFriendlyId}`;

      if (exception.code === 'USER_MUST_BE_OWNER_OF_POST') {
        message = 'You need to be owner of the live stream to start streaming';
        navigateTo = '/community';
      } else if (exception.code === 'TWO_MEETINGS_ON_ONE_BROWSER_WINDOW') {
        message = 'You are already in another meeting. Please end the current meeting first.';
      }

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message,
        seconds: 5,
        type: 'error'
      });
      application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
        to: navigateTo
      });

    }

  }

  mountLiveView() {

    this.liveView = new ProducerMode(this.meeting, this.post);

    setChildren(this.mainEl, [this.liveView]);

  }

  unmountLiveView() {

    if (this.liveView === null) {
      return;
    }

    unmount(this.mainEl, this.liveView);

    this.liveView = null;

  }

  mountEndedView() {

    this.producerModeEndedView = new ProducerModeEndedView(void 0, this.post);

    setChildren(this.mainEl, [this.producerModeEndedView]);

  }

  mountRoot() {

    if (this.meeting.status === meetingStatuses.LIVE) {
      this.mountLiveView();
    } else {
      this.mountEndedView();
    }

  }

}

export default SingleProducerMeetingStreamer;